import * as React from 'react';
import { animated, useSpring } from 'react-spring';
import ReactDOM from 'react-dom';
import { useMeasure } from '@epihub/web/src/hooks/useMeasure';
import { type Message } from '@krishanmadan/core/src/types';
import { reactSpringMessagesPortal } from '../../lib/HTMLComp';
import { styled } from '../../theme/theme';
import { ChatViewMessageBox } from '../ChatView/ChatViewMessageBox';
import { ChatViewMessageBoxAnimationContext } from './ChatViewMessageBoxAnimationContext';

type Props = {
  message: Message;
};

export const ChatViewMessageRow = React.memo(({ message }: Props) => {
  const {
    animatingMessageIds,
    animationOrigin,
    onAnimateMessageByIdComplete,
  } = React.useContext(ChatViewMessageBoxAnimationContext);

  const {
    measurementRef,
    measurements,
    measureRef,
  } = useMeasure();

  const isAnimating = animatingMessageIds[message.id];
  React.useEffect(() => {
    if (!isAnimating) {
      return;
    }

    const interval = setInterval(() => measureRef.current(), 300);
    return () => {
      clearInterval(interval);
    };
  }, [isAnimating]);

  const animation = useSpring({
    from: {
      opacity: 0.25,
      x: animationOrigin.x,
      y: animationOrigin.y,
    },
    to: {
      opacity: 1,
      x: measurements?.x ?? animationOrigin.x,
      y: measurements?.y ?? animationOrigin.y,
    },
    onRest: () => {
      onAnimateMessageByIdComplete(message.id);
    },
  });

  const left = message.role !== 'user';
  return (
    <Root>
      <LeftMessageSpacer left={left} />
      <ShadowWrapper
        ref={measurementRef}
        visible={!isAnimating}
      >
        <ChatViewMessageBox
          message={message}
          left={left}
        />
      </ShadowWrapper>
      {isAnimating && (
        <>
          {ReactDOM.createPortal(
            (
              <PortalWrapper>
                {/*@ts-ignore*/}
                <animated.div style={animation}>
                  <ChatViewMessageBox
                    message={message}
                    left={left}
                  />
                </animated.div>
              </PortalWrapper>
            ),
            reactSpringMessagesPortal,
          )}
        </>
      )}
    </Root>
  );
}, (previous, next) => {
  return previous.message.id != next.message.id;
});

export const Root = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  min-height: calc(86.3px + (2vh * 2));
  padding: 12px 0;
`;

export const LeftMessageSpacer = styled.div<{ left: boolean }>`
  ${({ left }) => (left ? '' : 'flex-grow: 1')};
  min-width: 0;
`;

export const PortalWrapper = styled.div`
  display: flex;
  position: absolute;
  width: 100vw;
`;

export const ShadowWrapper = styled.div<{ visible: boolean }>`
  position: relative;
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
`;
